import { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { format } from "date-fns";
import { z } from "zod";
import {
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Copy,
  Check,
  ArrowUp,
  ArrowDown,
  History,
  RotateCw,
  SkipForward,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCustomers } from "@/customers/useCustomers";
import { useEncounterWriteBacks } from "./useEncounterWriteBacks";
import { useErroredWriteBacks } from "./useErroredWriteBacks";
import { useMarkWriteBackSuccess } from "./useMarkWriteBackSuccess";
import { useRetryErroredWriteBacks } from "./useRetryErroredWriteBacks";
import { useRetrySingleWriteBack } from "./useRetrySingleWriteBack";
import { useWriteBackFilters, type SortColumn } from "./useWriteBackFilters";
import { useMarkWriteBackSkipped } from "./useMarkWriteBackSkipped";
import type { EncounterWriteBackGroup } from "@/api/writeBackManagement";

function copyToClipboard(text: string): void {
  if (navigator.clipboard?.writeText) {
    Promise.resolve(navigator.clipboard.writeText(text)).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text: string): void {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

const RETRY_ALL_RECODE_FILTER_ID = "write-back-retry-all-recode-note";
const RETRY_ALL_RECODE_FILTER_MESSAGE =
  'Retry All covers the whole queue, not the recodes shown. Clear "Recodes only" to use it.';

function RecodeMarker() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            role="img"
            aria-label="Recode: an earlier write-back for this encounter was already sent"
            className="inline-flex text-amber-600"
          >
            <History className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            Recode — an earlier write-back for this encounter was already sent. Sent is not the same as received by the
            trust.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function WriteBackManagementPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<{ encounterId: string; valid: boolean } | null>(null);
  const isSearching = search !== null;
  const [expandedEncounters, setExpandedEncounters] = useState<Set<string>>(new Set());

  const { customers = [] } = useCustomers();
  const customerOptions: Option[] = useMemo(() => customers.map((c) => ({ value: c.id, label: c.label })), [customers]);

  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    selectedCustomers,
    setSelectedCustomers,
    recodesOnly,
    setRecodesOnly,
    sortColumn,
    sortDirection,
    handleSort,
  } = useWriteBackFilters(customerOptions);

  const queryOptions = useMemo(
    () => ({
      customerIds: selectedCustomers.length ? selectedCustomers.map((c) => c.value) : undefined,
      dischargeDateFrom: fromDate ? format(fromDate, "yyyy-MM-dd") : undefined,
      dischargeDateTo: toDate ? format(toDate, "yyyy-MM-dd") : undefined,
    }),
    [selectedCustomers, fromDate, toDate],
  );

  const queueQuery = useErroredWriteBacks(queryOptions, !isSearching);
  const searchQuery = useEncounterWriteBacks(search?.valid ? search.encounterId : null);
  const encounters = isSearching ? (searchQuery.data ? [searchQuery.data] : []) : (queueQuery.data ?? []);
  const isLoading = isSearching ? searchQuery.isFetching : queueQuery.isLoading;
  const queryError = isSearching ? searchQuery.error : queueQuery.error;
  const refetch = isSearching ? searchQuery.refetch : queueQuery.refetch;
  const markSuccessMutation = useMarkWriteBackSuccess();
  const retryMutation = useRetryErroredWriteBacks();
  const retrySingleMutation = useRetrySingleWriteBack();
  const skipMutation = useMarkWriteBackSkipped();
  const [markingEncounterId, setMarkingEncounterId] = useState<string | null>(null);
  const [retryingEncounterId, setRetryingEncounterId] = useState<string | null>(null);

  const visibleEncounters = useMemo(
    () => (isSearching || !recodesOnly ? encounters : encounters.filter((group) => group.isRecode)),
    [encounters, isSearching, recodesOnly],
  );

  const sortedEncounters = useMemo(() => {
    if (isSearching) return visibleEncounters;

    const sorted = [...visibleEncounters].sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "customer":
          cmp = a.customer.label.localeCompare(b.customer.label);
          break;
        case "dischargeDate": {
          const da = a.dischargeDate ?? "";
          const db = b.dischargeDate ?? "";
          if (!da && !db) cmp = 0;
          else if (!da) cmp = 1;
          else if (!db) cmp = -1;
          else cmp = da.localeCompare(db);
          break;
        }
        case "specialty":
          cmp = a.specialty.localeCompare(b.specialty);
          break;
        case "writeBackCount": {
          const countA = a.writeBacks.filter((wb) => wb.status === "error").length;
          const countB = b.writeBacks.filter((wb) => wb.status === "error").length;
          cmp = countA - countB;
          break;
        }
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [visibleEncounters, sortColumn, sortDirection, isSearching]);

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  };

  const emptyMessage = isSearching
    ? `Encounter not found: ${search.encounterId}`
    : recodesOnly && encounters.length > 0
      ? `No recoded write-backs found. Clear "Recodes only" to see all ${encounters.length}.`
      : "No errored write-backs found.";

  const toggleExpanded = (encounterId: string) => {
    setExpandedEncounters((prev) => {
      const next = new Set(prev);
      if (next.has(encounterId)) {
        next.delete(encounterId);
      } else {
        next.add(encounterId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          {isSearching ? "Encounter write-backs" : "Write-Back Errors"}
          {!isSearching &&
            encounters.length > 0 &&
            (recodesOnly ? ` (${visibleEncounters.length} of ${encounters.length})` : ` (${encounters.length})`)}
        </h1>
        <div className="flex gap-2">
          {!isSearching && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={encounters.length === 0 || recodesOnly || retryMutation.isPending}
                  aria-describedby={recodesOnly ? RETRY_ALL_RECODE_FILTER_ID : undefined}
                >
                  <RotateCw className="mr-1 h-4 w-4" />
                  {retryMutation.isPending ? "Retrying..." : "Retry All"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Retry all errored encounters?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will re-enqueue {encounters.length} encounter(s) through the write-back pipeline. Each
                    encounter will be retried using the same context as its original write-back attempt.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => retryMutation.mutate(queryOptions)}>
                    Retry {encounters.length} encounter(s)
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" disabled={isLoading || (isSearching && !search.valid)} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const encounterId = searchInput.trim().toLowerCase();
          const valid = z.uuid().safeParse(encounterId).success;
          setSearch({ encounterId, valid });
          setExpandedEncounters(new Set(valid ? [encounterId] : []));
          if (valid && encounterId === search?.encounterId) void searchQuery.refetch();
        }}
      >
        <div className="space-y-1">
          <label htmlFor="write-back-encounter-id" className="text-sm font-medium">
            Encounter ID
          </label>
          <Input
            id="write-back-encounter-id"
            className="w-[360px] max-w-full"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Enter an encounter ID"
            aria-invalid={search !== null && !search.valid}
          />
        </div>
        <Button type="submit">Search</Button>
        {(isSearching || searchInput) && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSearchInput("");
              setSearch(null);
            }}
          >
            Clear search
          </Button>
        )}
      </form>
      {isSearching && (
        <p className="text-sm text-muted-foreground">Customer, date and sort filters are ignored while searching.</p>
      )}

      <fieldset
        disabled={isSearching}
        aria-label="Error queue filters"
        className="flex flex-wrap items-end gap-4 disabled:opacity-50"
      >
        <div className="space-y-1">
          <label className="block text-sm font-medium">Customer</label>
          <MultiSelect
            options={customerOptions}
            value={selectedCustomers}
            onChange={setSelectedCustomers}
            placeholder="All customers"
          />
        </div>

        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label className="block text-sm font-medium">Discharge from</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {fromDate ? format(fromDate, "dd/MM/yyyy") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={fromDate} onSelect={setFromDate} />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium">Discharge to</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {toDate ? format(toDate, "dd/MM/yyyy") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={toDate} onSelect={setToDate} />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex items-center gap-2 pb-2">
          <Checkbox
            id="write-back-recodes-only"
            checked={recodesOnly}
            onCheckedChange={(checked) => setRecodesOnly(checked === true)}
          />
          <label htmlFor="write-back-recodes-only" className="text-sm font-medium">
            Recodes only
          </label>
        </div>
      </fieldset>

      {recodesOnly && !isSearching && (
        <p id={RETRY_ALL_RECODE_FILTER_ID} className="text-sm text-muted-foreground">
          {RETRY_ALL_RECODE_FILTER_MESSAGE}
        </p>
      )}

      {search && !search.valid ? (
        <p role="alert">Enter a valid encounter ID (UUID).</p>
      ) : isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : queryError ? (
        <p role="alert">{queryError.message}</p>
      ) : visibleEncounters.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>
                <button
                  type="button"
                  className="cursor-pointer"
                  disabled={isSearching}
                  onClick={() => handleSort("customer")}
                >
                  Customer
                  <SortIcon column="customer" />
                </button>
              </TableHead>
              <TableHead>Encounter ID</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>
                <button
                  type="button"
                  className="cursor-pointer"
                  disabled={isSearching}
                  onClick={() => handleSort("dischargeDate")}
                >
                  Discharge Date
                  <SortIcon column="dischargeDate" />
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  className="cursor-pointer"
                  disabled={isSearching}
                  onClick={() => handleSort("specialty")}
                >
                  Specialty
                  <SortIcon column="specialty" />
                </button>
              </TableHead>
              <TableHead>
                <button
                  type="button"
                  className="cursor-pointer"
                  disabled={isSearching}
                  onClick={() => handleSort("writeBackCount")}
                >
                  Write-backs
                  <SortIcon column="writeBackCount" />
                </button>
              </TableHead>
              <TableHead>{isSearching ? "Write-back details" : "Latest Error"}</TableHead>
              <TableHead className="w-[160px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedEncounters.map((group) => (
              <EncounterRow
                key={group.encounterId}
                group={group}
                isSearchResult={isSearching}
                isExpanded={expandedEncounters.has(group.encounterId)}
                onToggle={() => toggleExpanded(group.encounterId)}
                onMarkSuccess={() => {
                  setMarkingEncounterId(group.encounterId);
                  markSuccessMutation.mutate(group.encounterId, {
                    onSettled: () => setMarkingEncounterId(null),
                  });
                }}
                isMarking={markingEncounterId === group.encounterId}
                onRetry={() => {
                  setRetryingEncounterId(group.encounterId);
                  retrySingleMutation.mutate(group.encounterId, {
                    onSettled: () => setRetryingEncounterId(null),
                  });
                }}
                isRetrying={retryingEncounterId === group.encounterId}
                onSkipWriteBack={(writeBackId: string) => {
                  skipMutation.mutate(writeBackId);
                }}
                isSkipping={skipMutation.isPending}
                skippingWriteBackId={skipMutation.variables ?? null}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function EncounterRow({
  group,
  isSearchResult,
  isExpanded,
  onToggle,
  onMarkSuccess,
  isMarking,
  onRetry,
  isRetrying,
  onSkipWriteBack,
  isSkipping,
  skippingWriteBackId,
}: {
  group: EncounterWriteBackGroup;
  isSearchResult: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onMarkSuccess: () => void;
  isMarking: boolean;
  onRetry: () => void;
  isRetrying: boolean;
  onSkipWriteBack: (writeBackId: string) => void;
  isSkipping: boolean;
  skippingWriteBackId: string | null;
}) {
  const navigate = useNavigate();
  const [copiedMrn, setCopiedMrn] = useState(false);
  const [copiedEncounterId, setCopiedEncounterId] = useState(false);
  // Rows are newest-attempt-first, but attempts sharing a created_at keep delivery order, so the
  // head of the group can be a delivered row carrying no message.
  const latestError = isSearchResult
    ? (group.writeBacks[0]?.errorMessage ?? "\u2014")
    : (group.writeBacks.find((wb) => wb.status === "error")?.errorMessage ?? "Unknown error");
  const canMarkSucceeded = !isSearchResult || group.writeBacks.some((writeBack) => writeBack.s3Key !== null);
  const errorCount = group.writeBacks.filter((wb) => wb.status === "error").length;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
        <TableCell>
          <Badge variant="outline">{group.customer.label}</Badge>
        </TableCell>
        <TableCell className="font-mono text-xs" onClick={(e) => e.stopPropagation()}>
          <span className="inline-flex items-center gap-1">
            <span
              className="cursor-pointer hover:underline"
              onClick={() => navigate(`/clinical-coding/unwritten-encounters/${group.encounterId}`)}
            >
              {isSearchResult ? group.encounterId : `${group.encounterId.slice(0, 8)}...`}
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => {
                copyToClipboard(group.encounterId);
                setCopiedEncounterId(true);
                setTimeout(() => setCopiedEncounterId(false), 1500);
              }}
            >
              {copiedEncounterId ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
            </button>
            {group.isRecode && <RecodeMarker />}
          </span>
        </TableCell>
        <TableCell
          className={`font-mono text-xs ${group.mrn ? "cursor-pointer" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (group.mrn) {
              copyToClipboard(group.mrn);
              setCopiedMrn(true);
              setTimeout(() => setCopiedMrn(false), 1500);
            }
          }}
        >
          <span className="inline-flex items-center gap-1">
            {group.mrn ?? "\u2014"}
            {group.mrn &&
              (copiedMrn ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              ))}
          </span>
        </TableCell>
        <TableCell>{group.dischargeDate ? format(new Date(group.dischargeDate), "dd/MM/yyyy") : "\u2014"}</TableCell>
        <TableCell className="capitalize">{group.specialty}</TableCell>
        <TableCell>
          {group.writeBacks.length === 0 ? (
            <span>No write-back attempts</span>
          ) : (
            <Badge variant={errorCount > 0 ? "destructive" : "secondary"}>{errorCount} errored</Badge>
          )}
          {group.writeBacks.length > errorCount && (
            <Badge variant="secondary" className="ml-1">
              {group.writeBacks.length - errorCount} other
            </Badge>
          )}
        </TableCell>
        <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{latestError}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            {!isSearchResult && (
              <Button size="sm" variant="outline" disabled={isRetrying} onClick={onRetry}>
                <RotateCw className="mr-1 h-3 w-3" />
                {isRetrying ? "Retrying..." : "Retry"}
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isMarking || !canMarkSucceeded}
                  title={!canMarkSucceeded ? "No delivery attempt to confirm" : undefined}
                >
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Mark Succeeded
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Mark as succeeded?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will mark the current delivery attempt for this encounter as successfully written back. Only do
                    this if you have manually completed the write-back.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onMarkSuccess}>Confirm</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>

      {isExpanded &&
        group.writeBacks.map((wb) => (
          <TableRow key={wb.id} className="bg-muted/50">
            <TableCell />
            <TableCell colSpan={3} className="font-mono text-xs">
              {wb.id.slice(0, 8)}...
            </TableCell>
            <TableCell>
              <Badge variant={wb.status === "error" ? "destructive" : "secondary"}>{wb.status}</Badge>
            </TableCell>
            <TableCell>{wb.humanReviewId ? "Human review" : "Classification result"}</TableCell>
            <TableCell colSpan={2} className="text-xs text-muted-foreground">
              {wb.errorMessage ?? "\u2014"}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              {(wb.status === "error" || (isSearchResult && ["pending", "rejected"].includes(wb.status))) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" disabled={isSkipping && skippingWriteBackId === wb.id}>
                      <SkipForward className="mr-1 h-3 w-3" />
                      {isSkipping && skippingWriteBackId === wb.id ? "Skipping..." : "Skip"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mark as skipped?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This marks this write-back as &quot;already coded by customer&quot;. It will no longer appear as
                        an error and will count as skipped in dashboard metrics.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onSkipWriteBack(wb.id)}>Confirm</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
}
